// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Avatar/Icon are still JS
// @ts-nocheck — remove once Avatar and Icon are converted to TypeScript
import type { ComponentProps, ReactNode } from "react";
import styled, { css } from "styled-components";
import { themeGet } from "@styled-system/theme-get";

import Avatar from "../Avatar";
import Icon from "../Icon";

export type SeatAvatarStatus = "open" | "requisition" | "filled";
export type SeatAvatarRing = "dashed" | "solid";
type SeatAvatarSizing = "small" | "default" | "large";

export type SeatAvatarProps = Omit<
  ComponentProps<typeof Avatar>,
  "sizing" | "initials" | "image"
> & {
  /**
   * open — ghost face, faded person glyph. requisition — ghost face,
   * person-with-magnifier glyph. filled — the occupant's image/initials.
   */
  status?: SeatAvatarStatus;
  /** Dashed reads as vacant; solid marks a seat whose occupant is not shown. */
  ring?: SeatAvatarRing;
  sizing?: SeatAvatarSizing;
  /** Occupant photo — only rendered when status is "filled". */
  image?: string;
  /** Occupant initials — only rendered when status is "filled". */
  initials?: ReactNode;
};

const STATUS_ICON: Record<Exclude<SeatAvatarStatus, "filled">, string[]> = {
  open: ["far", "user"],
  requisition: ["far", "user-magnifying-glass"],
};

// Open seats fade the glyph so the ring, not the person, carries the weight.
export const OPEN_GLYPH_OPACITY = 0.3;

const ICON_SIZE_BY_SIZING: Record<SeatAvatarSizing, string> = {
  small: "sm",
  default: "1x",
  large: "2x",
};

const seatRing = css`
  box-sizing: border-box;
  border: ${themeGet("borderWidths.2")} ${({ $ring }) => $ring}
    ${themeGet("colors.warning")};
`;

const ghostFace = css`
  background: transparent;
  background-image: none;
  color: ${themeGet("colors.warning")};

  svg {
    color: ${themeGet("colors.warning")};
  }
`;

const StyledSeatAvatar = styled(Avatar)`
  [data-avatar-part="image"] {
    ${seatRing}
  }

  [data-avatar-part="face"] {
    ${seatRing}
    ${({ $ghost }) => ($ghost ? ghostFace : "")}
  }

  &[data-seat-status="open"] [data-avatar-part="face"] svg {
    opacity: ${OPEN_GLYPH_OPACITY};
  }
`;

/**
 * Avatar for a position seat: amber ring, with a glyph for the seat's state or
 * the occupant's image when filled. Same sizing scale as Avatar.
 */
const SeatAvatar = ({
  status = "open",
  ring = "dashed",
  sizing = "default",
  image,
  initials,
  ...props
}: SeatAvatarProps) => {
  const isGhost = status !== "filled";

  return (
    <StyledSeatAvatar
      {...props}
      data-seat-status={status}
      sizing={sizing}
      image={isGhost ? undefined : image}
      initials={
        isGhost ? (
          <Icon icon={STATUS_ICON[status]} size={ICON_SIZE_BY_SIZING[sizing]} />
        ) : (
          initials
        )
      }
      $ring={ring}
      $ghost={isGhost}
    />
  );
};

export default SeatAvatar;
